// from
// https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/containers/AttachmentSettingModal/index.tsx

import {
  Box,
  Button,
  ConfirmationModal,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
} from '@modusign/design-system';
import { useAttachments } from '@modusign/react-signing';
import { Attachment } from '@modusign/react-signing/types';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-v7';
import { useToggle } from 'react-use';
import { v4 } from 'uuid';

import AttachmentRequestCard from '@app/components/AttachmentRequestCard';
import MaxAttachmentAlertModal from '@app/components/Modal/MaxAttachmentAlertModal';
import AttachmentExceededAlertModal from '@app/containers/Modal/AttachmentExceededAlertModal';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import useUsageCount from '@app/hooks/useUsageCount';

export const DOCUMENT_MAX_ATTACHMENTS_COUNT = 30;
interface AttachmentRequest {
  attachmentType: string;
  dataLabel?: string;
  message: string | null;
  required: boolean;
}

interface AttachmentSettingModalProps {
  isOpen: boolean;
  showDataLabelInput: boolean;
  needAuthorityCheck: boolean;
  signerAttachments: Attachment[];
  signerId: string;
  signerName: string;
  onModalClose: () => void;
}

const defaultProps = {
  showDataLabelInput: false,
  needAuthorityCheck: false,
  signerId: '',
  signerName: '',
  onModalClose() {},
  onSaveAttachmentRequest() {},
};

export type AttachmentSettingFormValues = Record<string, AttachmentRequest>;

function AttachmentSettingModal({
  isOpen,
  showDataLabelInput,
  needAuthorityCheck,
  signerAttachments,
  signerId,
  signerName,
  onModalClose,
}: AttachmentSettingModalProps) {
  // TODO TemplateAttachmentSettingModal 에 있는 option 반영
  // TODO react-signing 연결 마무리
  const { attachments, removeAttachment, addAttachment } = useAttachments();
  const { attachmentRequestCount } = useUsageCount();

  const [isAttachmentExceededAlertModalOpen, toggleAttachmentExceededAlertModalOpen] =
    useToggle(false);
  const [isMaxAttachmentAlertModalOpen, toggleMaxAttachmentAlertModalOpen] = useToggle(false);
  const [isExitConfirmationModalOpen, toggleExitConfirmationModalOpen] = useToggle(false);

  const [attachmentRequestIds, setAttachmentRequestIds] = useState<string[]>(() => {
    return signerAttachments.length > 0 ? signerAttachments.map(({ id }) => id) : [v4()];
  });

  const otherAttachmentsCount = attachments.filter(
    (a) => !attachmentRequestIds.includes(a.id)
  ).length;
  const totalAttachmentsCount = otherAttachmentsCount + attachmentRequestIds.length;
  const attachmentsOfCurrentSigner = useMemo<AttachmentRequest[]>(() => {
    return signerAttachments.map((attachment) => ({
      attachmentType: attachment.title,
      dataLabel: attachment.dataLabel,
      message: attachment.message,
      required: attachment.config.required,
    }));
  }, [signerAttachments]);

  const formMethods = useForm<AttachmentSettingFormValues>({
    mode: 'onChange',
    defaultValues: attachmentRequestIds.reduce<AttachmentSettingFormValues>((acc, id, index) => {
      acc[id] = attachmentsOfCurrentSigner[index];
      return acc;
    }, {}),
  });

  const othersDataLabels = useMemo(() => {
    const dataLabels = attachments
      .filter(
        (attachment) =>
          attachment.participantReference.id !== signerId &&
          attachment.participantReference.type === 'SIGNER'
      )
      .map(({ dataLabel }) => dataLabel ?? '');

    return dataLabels;
  }, [attachments, signerId]);

  function handleClickAddAttachmentRequest() {
    if (needAuthorityCheck && attachmentRequestCount.limit <= totalAttachmentsCount) {
      toggleAttachmentExceededAlertModalOpen();
    } else if (totalAttachmentsCount >= DOCUMENT_MAX_ATTACHMENTS_COUNT) {
      toggleMaxAttachmentAlertModalOpen();
    } else {
      setAttachmentRequestIds([...attachmentRequestIds, v4()]);
    }
  }

  function handleClickCancelAttachmentSettingModal() {
    toggleExitConfirmationModalOpen();
  }

  function handleDeleteAttachmentRequestCard(paramId: string) {
    setAttachmentRequestIds(attachmentRequestIds.filter((id) => id !== paramId));
  }

  function handleSaveAttachmentRequest(data: AttachmentSettingFormValues) {
    if (totalAttachmentsCount > DOCUMENT_MAX_ATTACHMENTS_COUNT) {
      toggleMaxAttachmentAlertModalOpen();
      return;
    }

    attachments
      .filter((item) => item.participantReference.id === signerId)
      .forEach((a) => removeAttachment(a.id));

    attachmentRequestIds.forEach((id) => {
      const target = data[id];
      addAttachment({
        id,
        participantId: signerId,
        message: target.message,
        title: target.attachmentType.trim(),
        dataLabel: target.dataLabel || createDataLabel(),
        config: {
          required: target.required,
        },
      });
    });

    onModalClose();
  }

  if (!isOpen) {
    return null;
  }

  const name = signerName || '서명자';

  return (
    <>
      <Modal isOpen maxWidth="md" onClose={onModalClose}>
        <ModalTitle mb={4}>{`${name}님에게 첨부파일 요청`}</ModalTitle>
        <ModalContent gap={7}>
          <Text textAlign="center">
            {name}
            님에게 요청할 파일 정보를 입력해 주세요.
          </Text>
          <Flex flexDirection="column" gap={6} pt={2} px={3}>
            <FormProvider {...formMethods}>
              {attachmentRequestIds.map((id, index) => (
                <AttachmentRequestCard
                  id={id}
                  index={index}
                  key={id}
                  othersDataLabels={othersDataLabels}
                  showDataLabelInput={showDataLabelInput}
                  onDeleteClick={handleDeleteAttachmentRequestCard}
                />
              ))}
              <Button
                bg="buttonBackgroundNeutralLightest"
                variant="subtle"
                onClick={handleClickAddAttachmentRequest}
              >
                + 파일요청 추가하기
              </Button>
            </FormProvider>
          </Flex>
        </ModalContent>
        <ModalFooter>
          <Button mr={3} onClick={handleClickCancelAttachmentSettingModal}>
            취소
          </Button>
          <Button
            disabled={!formMethods.formState.isValid}
            variant="primary"
            onClick={formMethods.handleSubmit(handleSaveAttachmentRequest)}
          >
            설정하기
          </Button>
        </ModalFooter>
      </Modal>
      {isMaxAttachmentAlertModalOpen && (
        <MaxAttachmentAlertModal
          count={DOCUMENT_MAX_ATTACHMENTS_COUNT}
          onConfirm={() => toggleMaxAttachmentAlertModalOpen()}
        />
      )}
      <ConfirmationModal
        confirmButtonLabel="확인"
        isOpen={isExitConfirmationModalOpen}
        maxWidth="xxs"
        title="설정 안내"
        onClose={() => toggleExitConfirmationModalOpen()}
        onConfirm={() => {
          toggleExitConfirmationModalOpen();
          onModalClose();
        }}
      >
        <Box textAlign="center">
          이 창을 닫으면 방금 변경한 정보가
          <br />
          저장되지 않습니다.
        </Box>
      </ConfirmationModal>
      {isAttachmentExceededAlertModalOpen && (
        <AttachmentExceededAlertModal onConfirm={toggleAttachmentExceededAlertModalOpen} />
      )}
    </>
  );
}

AttachmentSettingModal.defaultProps = defaultProps;

export default AttachmentSettingModal;
